import { type MouseEvent, useCallback } from 'react';
import { useNavigate } from 'react-router';

/**
 * Keeps the side nav's logo an SPA transition.
 *
 * `SideNavBar` renders its logo as a plain `<a href>` with no `asChild` escape
 * hatch, so `logoHref` hard-navigates and the flag fetch and project cache go
 * with it. Intercepting on the way up keeps the `href`, so hover, middle-click
 * and open-in-new-tab still behave. Delegated from the `<aside>` because the
 * anchor cannot take a handler directly.
 */
export const useSideNavLogoNavigation = (to: string) => {
  const navigate = useNavigate();

  return useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (event.defaultPrevented) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      if (!(event.target instanceof Element)) return;
      if (!event.target.closest('[data-ds-side-nav-logo]')) return;

      event.preventDefault();
      navigate(to);
    },
    [navigate, to],
  );
};
